import { readonly, type Ref } from 'vue'
import type {
    CatalogueEngagementRingFragment,
    CatalogueReadyToGoRing,
    JewelleryDetailsFragment,
    ProductCardFragment,
    WeddingRingFragment,
} from '@/graphql/api'
import {
    getDescription,
    getName,
} from '@/components/layout/cards/ProductCard.vue'
import { useAbsoluteUrl } from '@/composables/app/useAbsoluteUrl'
import { useRoutes } from '@/router/routes'
import { useObjectCookie } from '@/plugins/cookie-plugin'

export type SaveabledProduct =
    | ProductCardFragment
    | CatalogueEngagementRingFragment
    | WeddingRingFragment
    | CatalogueReadyToGoRing
    | JewelleryDetailsFragment

type PersistedData = {
    name: string
    description: string
    image: string
    link: string
}

type UseSaveProductButton = {
    addProduct: (product: SaveabledProduct) => void
    removeProduct: (product: SaveabledProduct) => void
    getIsProductSaved: (product: SaveabledProduct) => boolean
}

type UseSavedProductsModal = {
    savedProducts: Ref<Readonly<PersistedData[]>>
    removeProduct: (index: number) => void
}

const useSavedProductsStore = () => {
    const savedProducts = useObjectCookie<PersistedData[]>('savedProducts', [])

    return { savedProducts }
}

export function useSaveProductButton(): UseSaveProductButton {
    const absoluteUrl = useAbsoluteUrl()
    const routes = useRoutes()

    const createLink = (product: SaveabledProduct): string =>
        absoluteUrl(routes.ring.product(product)).href

    const createStoredData = (product: SaveabledProduct): PersistedData => ({
        name: getName(product),
        description: getDescription(product) || '',
        image: product.images[0]?.url || '',
        link: createLink(product),
    })

    const { savedProducts } = useSavedProductsStore()

    const addProduct = (product: SaveabledProduct): void => {
        if (savedProducts.value.length >= 10) {
            alert('You have reached maximum saved products of 10!')
        } else {
            savedProducts.value = [
                createStoredData(product),
                ...savedProducts.value,
            ]
        }
    }

    const removeProduct = (product: SaveabledProduct): void => {
        const productLink = createLink(product)

        savedProducts.value = savedProducts.value.filter(
            (product) => product.link !== productLink
        )
    }

    const getIsProductSaved = (product: SaveabledProduct): boolean => {
        const productLink = createLink(product)

        return savedProducts.value.some(
            (product) => product.link === productLink
        )
    }

    return {
        addProduct,
        removeProduct,
        getIsProductSaved,
    }
}

export function useSavedProductsModal(): UseSavedProductsModal {
    const { savedProducts } = useSavedProductsStore()
    const removeProduct = (index: number) =>
        savedProducts.value.splice(index, 1)

    return {
        savedProducts: readonly(savedProducts),
        removeProduct,
    }
}
